import React from 'react';
import { Box, Flex, FlexProps, Image, Text } from '@chakra-ui/react';

export interface ColumnDef {
  label: string;
  width: string;
  minWidth?: string;
  paddingLeft?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: () => void;
  sortArrowSrc?: string;
  sortArrowAlt?: string;
}

export interface GenericTableProps extends Pick<FlexProps, 'mt'> {
  columns: ColumnDef[];
  children: React.ReactNode; // rendered rows
  emptyText: string;
  isEmpty: boolean;
  tableWidth?: string;
}

const HEADER_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '120%',
  letterSpacing: '0.01em',
  color: '#FFFFFF',
};

const CELL_TEXT_STYLE = {
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.01em',
  color: '#00292B',
};

export const GenericTable: React.FC<GenericTableProps> = ({
  columns,
  children,
  emptyText,
  isEmpty,
  tableWidth = '1040px',
  mt,
}) => (
  <Box w={tableWidth} mt={mt}>
    {/* Header */}
    <Flex w={tableWidth} h="61px" bg="#00AAFF">
      {columns.map((col, i) => (
        <Flex
          key={i}
          w={col.width}
          minW={col.minWidth ?? col.width}
          align="center"
          pl={col.paddingLeft ?? '12px'}
          pr="12px"
          gap="4px"
          borderBottom="2px solid #006699"
          cursor={col.onSort ? 'pointer' : 'default'}
          onClick={col.onSort}
          aria-label={col.sortArrowAlt}
        >
          <Text {...HEADER_TEXT_STYLE}>{col.label}</Text>
          {col.onSort && col.sortArrowSrc && (
            <Image
              src={col.sortArrowSrc}
              alt={col.sortArrowAlt}
              w="18px"
              h="18px"
              transform={col.sortDirection === 'desc' ? 'scaleY(1)' : 'scaleY(-1)'}
            />
          )}
        </Flex>
      ))}
    </Flex>

    {/* Body */}
    <Flex direction="column" w={tableWidth}>
      {isEmpty ? (
        <Box w={tableWidth} borderBottom="1px solid #00AAFF" px="40px" py="24px">
          <Text {...CELL_TEXT_STYLE}>{emptyText}</Text>
        </Box>
      ) : (
        children
      )}
    </Flex>
  </Box>
);

export default GenericTable;
